import { Target, Compass, Heart, Shield, Users, Award } from "lucide-react";
import { aboutTeam, leadership } from "@/assets/images";

const TEAM_IMG = aboutTeam;
const LEADER_IMG = leadership;

const VALUES = [
  { icon: Shield, t: "Trust by default", d: "Security, reliability, and data integrity sit at the core of every product we build." },
  { icon: Users, t: "Customer-first", d: "We partner deeply with our customers — their workflow wins are our product roadmap." },
  { icon: Award, t: "Craft & quality", d: "We believe enterprise software should feel as refined as the best consumer products." },
  { icon: Heart, t: "Team & empathy", d: "We invest in our people, our community, and the long-term health of our ecosystem." },
];

const WHY = [
  "Purpose-built platforms for verticals we deeply understand",
  "Enterprise-grade security, compliance, and scalability from day one",
  "Obsessive focus on user experience and workflow design",
  "Transparent, partnership-driven engagement model",
  "Global cloud infrastructure with India-rooted engineering",
];

const About = () => {
  return (
    <div data-testid="page-about">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-section py-16 md:py-24">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">About Ortelly</span>
          <h1 className="mt-3 max-w-4xl text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            We build <span className="brand-gradient-text">products</span> that make modern businesses move faster.
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">
            Ortelly Systems Private Limited is an India-headquartered product company focused on enterprise-grade software for creative and operational workflows. We exist to build tools that respect the intelligence of their users and reward the craft of great work.
          </p>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="container-section py-16 md:py-24">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-md border border-slate-200 bg-white p-8 shadow-sm">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]">
              <Compass className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-semibold text-slate-900">Our Vision</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              To be the default operating system for the world's creative and operational workflows — a place where teams collaborate, automate, and deliver extraordinary outcomes.
            </p>
          </div>
          <div className="rounded-md border border-slate-200 bg-white p-8 shadow-sm">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#00C4B5]/15 text-[#00C4B5]">
              <Target className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-semibold text-slate-900">Our Mission</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              To build beautifully designed, deeply integrated vertical SaaS products that replace tool sprawl with elegant, end-to-end platforms — starting with photography through PixTraya.
            </p>
          </div>
        </div>
      </section>

      {/* Team image */}
      <section className="container-section pb-16 md:pb-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Leadership Mindset</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Builders, operators, and long-term thinkers.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Our leadership team combines decades of experience across enterprise SaaS, cloud infrastructure, and creative industries. We believe in small, empowered teams, transparent decisions, and shipping things we'd be proud to use ourselves.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <img src={LEADER_IMG} alt="Leadership" className="h-14 w-14 rounded-full object-cover" />
              <div>
                <p className="text-sm font-semibold text-slate-900">The Ortelly Leadership Team</p>
                <p className="text-xs text-slate-500">Engineering • Product • Customer Success</p>
              </div>
            </div>
          </div>
          <div className="order-1 overflow-hidden rounded-lg border border-slate-200 shadow-sm lg:order-2">
            <img src={TEAM_IMG} alt="Ortelly team" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="container-section py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Core Values</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              The principles that shape how we build and operate.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <div key={v.t} data-testid={`value-${v.t.toLowerCase().replace(/\s+/g,'-')}`} className="rounded-md border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0046B5]/10 text-[#0046B5]">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{v.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ortelly */}
      <section className="container-section py-16 md:py-24">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0046B5]">Why choose Ortelly</span>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              A partner built for the long run.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              When you partner with Ortelly, you're working with a team that invests in your workflow, your people, and your outcomes — not just a software license.
            </p>
          </div>
          <ul className="space-y-4">
            {WHY.map((w, i) => (
              <li key={w} className="flex items-start gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md brand-gradient text-sm font-semibold text-white">{i + 1}</span>
                <p className="text-sm leading-relaxed text-slate-700">{w}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default About;
